import { useCallback, useMemo } from "react";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import type { FilterEntityType } from "@/lib/filters/types";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Storage version for schema compatibility
const STORAGE_VERSION = "v1";

type SalespersonFilterObject = Extract<
  FilterEntityType,
  "account" | "opportunity" | "touchpoint"
>;

interface SalespersonFiltersState {
  account: FilterGroup;
  opportunity: FilterGroup;
  touchpoint: FilterGroup;
}

const defaultState: SalespersonFiltersState = {
  account: EMPTY_GROUP,
  opportunity: EMPTY_GROUP,
  touchpoint: EMPTY_GROUP,
};

/**
 * Hook to manage salesperson filter state for Account, Opportunity & Touchpoint objects.
 *
 * - Persists state to sessionStorage for tab-specific isolation.
 * - Provides setters and clear helpers for each individual object type.
 * - Exposes convenience helpers for serialising the filters into the single
 *   `filters` query-parameter understood by the backend.
 */
export function useSalespersonFilterState(scope: string) {
  // Use sessionStorage for tab-specific filter isolation
  // Migrated from filters::v1 to metrics::filters::v1 for clarity
  const storageKey = `${scope}::metrics::filters::${STORAGE_VERSION}`;

  // ---------------------------------------------------------------------------
  // Persistence layer
  // ---------------------------------------------------------------------------

  const [state, setState] = useTrackedSessionStorage<SalespersonFiltersState>(
    storageKey,
    defaultState,
    {
      // Migrate from old location
      migration: {
        from: {
          key: `${scope}::filters::${STORAGE_VERSION}`,
          storage: "sessionStorage",
          organizationScoped: true,
        },
        cleanup: true, // Remove the old key after migration
      },
    },
  );

  // ---------------------------------------------------------------------------
  // Update helpers
  // ---------------------------------------------------------------------------

  // Utility function to safely update state with proper fallbacks
  const updateFilterState = useCallback(
    (updates: Partial<SalespersonFiltersState>) => {
      setState((prev) => ({
        account: updates.account ?? prev?.account ?? EMPTY_GROUP,
        opportunity: updates.opportunity ?? prev?.opportunity ?? EMPTY_GROUP,
        touchpoint: updates.touchpoint ?? prev?.touchpoint ?? EMPTY_GROUP,
      }));
    },
    [setState],
  );

  const setAccountFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ account: filters }),
    [updateFilterState],
  );

  const setOpportunityFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ opportunity: filters }),
    [updateFilterState],
  );

  const setTouchpointFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ touchpoint: filters }),
    [updateFilterState],
  );

  const clearAccountFilters = useCallback(() => {
    updateFilterState({ account: EMPTY_GROUP });
  }, [updateFilterState]);

  const clearOpportunityFilters = useCallback(() => {
    updateFilterState({ opportunity: EMPTY_GROUP });
  }, [updateFilterState]);

  const clearTouchpointFilters = useCallback(() => {
    updateFilterState({ touchpoint: EMPTY_GROUP });
  }, [updateFilterState]);

  const clearAllFilters = useCallback(() => {
    setState({
      account: EMPTY_GROUP,
      opportunity: EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
    });
  }, [setState]);

  // ---------------------------------------------------------------------------
  // Serialisation helpers – returns undefined when the group is effectively empty
  // ---------------------------------------------------------------------------

  const serialize = useCallback(
    (obj: SalespersonFilterObject): string | undefined => {
      let group: FilterGroup;
      switch (obj) {
        case "account":
          group = state.account;
          break;
        case "opportunity":
          group = state.opportunity;
          break;
        case "touchpoint":
          group = state.touchpoint;
          break;
        default:
          throw new Error(`Unknown filter object: ${obj}`);
      }
      // Empty group results in `undefined` so we omit the query param entirely
      if (!group || group.filters.length === 0) return undefined;
      try {
        return JSON.stringify(group);
      } catch (error) {
        console.error("Failed to serialize filter group:", error);
        return undefined;
      }
    },
    [state],
  );

  // Quick flags for UI badges etc.
  const hasAccountFilters = useMemo(
    () => state?.account?.filters?.length > 0,
    [state.account],
  );
  const hasOpportunityFilters = useMemo(
    () => state?.opportunity?.filters?.length > 0,
    [state.opportunity],
  );
  const hasTouchpointFilters = useMemo(
    () => state?.touchpoint?.filters?.length > 0,
    [state.touchpoint],
  );

  return {
    accountFilters: state?.account ?? EMPTY_GROUP,
    opportunityFilters: state?.opportunity ?? EMPTY_GROUP,
    touchpointFilters: state?.touchpoint ?? EMPTY_GROUP,
    setAccountFilters,
    setOpportunityFilters,
    setTouchpointFilters,
    clearAccountFilters,
    clearOpportunityFilters,
    clearTouchpointFilters,
    clearAllFilters,
    serialize,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
  };
}
